'use client';

import NextLink from 'next/link';
import {
  useParams as useNextParams,
  useSearchParams as useNextSearchParams,
  usePathname,
  useRouter,
} from 'next/navigation';
import {
  type AnchorHTMLAttributes,
  type PropsWithChildren,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';

type HashLocation = {
  hash: string;
  pathname: string;
  search: string;
};

type NavigateOptions = {
  replace?: boolean;
};

type SearchParamsInit =
  | URLSearchParams
  | Record<string, string | string[] | number | null | undefined>
  | string
  | Array<[string, string]>;

type NextPrefetchBehavior = boolean | 'auto' | null;
type RouterPrefetchBehavior = 'intent' | 'render' | 'none' | 'viewport';

type To =
  | string
  | {
      hash?: string;
      pathname?: string;
      search?: string;
    };

function getWindowLocation(): HashLocation {
  if (typeof window === 'undefined') {
    return {
      hash: '',
      pathname: '/',
      search: '',
    };
  }

  return {
    hash: window.location.hash,
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

function normalizeToString(to: To) {
  if (typeof to === 'string') {
    return to;
  }

  return `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`;
}

function resolveTo(to: To, location: HashLocation) {
  const target = normalizeToString(to);

  if (
    target.startsWith('/') ||
    target.startsWith('?') ||
    target.startsWith('#')
  ) {
    if (target.startsWith('?')) {
      return `${location.pathname}${target}`;
    }

    if (target.startsWith('#')) {
      return `${location.pathname}${location.search}${target}`;
    }

    return target;
  }

  const basePath = location.pathname.endsWith('/')
    ? location.pathname.slice(0, -1)
    : location.pathname;

  return `${basePath}/${target}`.replace(/\/+/g, '/');
}

function buildSearchParams(init: SearchParamsInit) {
  if (init instanceof URLSearchParams) {
    return new URLSearchParams(init);
  }

  if (typeof init === 'string') {
    return new URLSearchParams(init.startsWith('?') ? init.slice(1) : init);
  }

  if (Array.isArray(init)) {
    return new URLSearchParams(init);
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(init)) {
    if (value == null) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        searchParams.append(key, String(item));
      }
      continue;
    }

    searchParams.set(key, String(value));
  }

  return searchParams;
}

function isModifiedEvent(
  event: Pick<MouseEvent, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>
) {
  return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}

function useSafeRouter() {
  try {
    return useRouter();
  } catch {
    return null;
  }
}

function resolvePrefetchBehavior(
  prefetch: LinkProps['prefetch']
): NextPrefetchBehavior | undefined {
  if (prefetch === 'none' || prefetch === 'intent') {
    return false;
  }

  if (prefetch === 'render' || prefetch === 'viewport') {
    return 'auto';
  }

  return prefetch;
}

function useSafePathname() {
  try {
    return usePathname();
  } catch {
    return null;
  }
}

function useSafeSearchParams() {
  try {
    return useNextSearchParams();
  } catch {
    return null;
  }
}

function useSafeParams() {
  try {
    return useNextParams<Record<string, string | string[]>>();
  } catch {
    return {};
  }
}

/**
 * The fragment, which Next does not expose through a hook of its own.
 *
 * It re-syncs on every client navigation, not only on `hashchange` and
 * `popstate`: a `router.push` to a URL without a fragment fires neither event,
 * so a hash-only subscription kept publishing the fragment of the page the user
 * had already left. `pathname` and `search` are the effect's change signal
 * because they are what Next re-renders this hook's caller with, and the effect
 * runs after Next has committed its `history.pushState`, so `window.location`
 * is current by the time it reads it.
 */
function useHash(pathname: string | null, search: string) {
  const [hash, setHash] = useState(() => getWindowLocation().hash);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncHash = () => {
      setHash(window.location.hash);
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);
    window.addEventListener('popstate', syncHash);

    return () => {
      window.removeEventListener('hashchange', syncHash);
      window.removeEventListener('popstate', syncHash);
    };
  }, [pathname, search]);

  return hash;
}

export function useLocation() {
  const pathname = useSafePathname();
  const nextSearchParams = useSafeSearchParams();
  const fallbackLocation = getWindowLocation();
  /*
    An EMPTY parameter set is an ANSWER, not a missing one.

    `useSafeSearchParams` returns `null` only when `next/navigation`'s hook is
    unavailable at all — outside an App Router tree, which is the one case the
    `window.location` fallback exists for. Treating zero parameters as "unknown"
    as well is what made every navigation that CLEARS the whole query string
    need a second click: Next commits its `history.pushState` from a commit
    effect, so during the render that follows a client navigation
    `window.location.search` still holds the OUTGOING query. The fallback then
    re-published the previous search, this hook's memo saw no change, and
    nothing downstream re-rendered — the surface only caught up on the next
    unrelated navigation.

    It was not a work-queues bug. It hit the queue section tabs (returning to
    the queue's first section omits `section` entirely), the
    All / Mine / Unassigned scope toggle, and every other control whose default
    state serialises to no query string at all.
  */
  const nextSearch = nextSearchParams?.toString();
  let search: string;

  if (nextSearchParams === null) {
    search = fallbackLocation.search;
  } else {
    search = nextSearch ? `?${nextSearch}` : '';
  }

  const hash = useHash(pathname, search);

  return useMemo(
    () => ({
      hash,
      key: `${pathname ?? fallbackLocation.pathname}${search}${hash}`,
      pathname: pathname ?? fallbackLocation.pathname,
      search,
      state: null,
    }),
    [fallbackLocation.pathname, hash, pathname, search]
  );
}

export function useNavigate() {
  const router = useSafeRouter();
  const location = useLocation();

  return useMemo(() => {
    return (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        if (typeof window !== 'undefined') {
          window.history.go(to);
        }
        return;
      }

      const href = resolveTo(to, location);

      if (router) {
        if (options?.replace) {
          router.replace(href);
        } else {
          router.push(href);
        }
        return;
      }

      if (typeof window !== 'undefined') {
        if (options?.replace) {
          window.location.replace(href);
        } else {
          window.location.assign(href);
        }
      }
    };
  }, [location, router]);
}

export function useParams<T extends Record<string, string | undefined>>() {
  const nextParams = useSafeParams();

  return useMemo(() => {
    return Object.fromEntries(
      Object.entries(nextParams).map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join('/') : value,
      ])
    ) as T;
  }, [nextParams]);
}

export function useSearchParams() {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const setSearchParams = (
    nextInit:
      | SearchParamsInit
      | ((previous: URLSearchParams) => SearchParamsInit | URLSearchParams),
    options?: NavigateOptions
  ) => {
    const resolvedInit =
      typeof nextInit === 'function'
        ? nextInit(new URLSearchParams(searchParams))
        : nextInit;
    const nextSearchParams = buildSearchParams(resolvedInit);
    const query = nextSearchParams.toString();

    navigate(
      `${location.pathname}${query ? `?${query}` : ''}${location.hash}`,
      options
    );
  };

  return [searchParams, setSearchParams] as const;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  prefetch?: NextPrefetchBehavior | RouterPrefetchBehavior;
  replace?: boolean;
  to: To;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    onClick,
    onFocus,
    onMouseEnter,
    onTouchStart,
    prefetch,
    replace,
    target,
    to,
    ...props
  },
  ref
) {
  const router = useSafeRouter();
  const location = useLocation();
  const href = resolveTo(to, location);
  const nextPrefetch = resolvePrefetchBehavior(prefetch);

  // The codebase type-checks against react-router but runs through this
  // Next-backed compatibility layer. Normalize react-router's prefetch modes
  // so callers can use that API without opting into unsupported Next preload
  // behavior on always-visible dashboard drill-down links.
  const prefetchOnIntent = () => {
    if (!router || prefetch !== 'intent' || target === '_blank') {
      return;
    }

    void router.prefetch(href);
  };

  if (!router) {
    return (
      <a
        {...props}
        href={href}
        onClick={onClick}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onTouchStart={onTouchStart}
        ref={ref}
        target={target}
      />
    );
  }

  return (
    <NextLink
      {...props}
      href={href}
      onClick={(event) => {
        onClick?.(event);

        if (
          event.defaultPrevented ||
          target === '_blank' ||
          isModifiedEvent(event.nativeEvent)
        ) {
          return;
        }
      }}
      onFocus={(event) => {
        onFocus?.(event);

        if (!event.defaultPrevented) {
          prefetchOnIntent();
        }
      }}
      onMouseEnter={(event) => {
        onMouseEnter?.(event);

        if (!event.defaultPrevented) {
          prefetchOnIntent();
        }
      }}
      onTouchStart={(event) => {
        onTouchStart?.(event);

        if (!event.defaultPrevented) {
          prefetchOnIntent();
        }
      }}
      prefetch={nextPrefetch}
      ref={ref}
      replace={replace}
      target={target}
    />
  );
});

export function Navigate({
  replace,
  to,
}: {
  replace?: boolean;
  to: To;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, replace, to]);

  return null;
}

export function Outlet({ children }: PropsWithChildren) {
  return <>{children ?? null}</>;
}
